import {
  Alert,
  Button,
  FileInput,
  Group,
  Modal,
  Select,
  Stack,
  Textarea,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { IconAlertCircle, IconUpload } from "@/lib/icons";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import {
  type FunctionalTestKind,
  uploadFunctionalTest,
} from "@/lib/api/functional_tests";
import { ApiError } from "@/lib/api/client";

const KINDS: FunctionalTestKind[] = [
  "ecg",
  "xray_chest",
  "ultrasound",
  "spirography",
  "audiometry",
];

export function FunctionalTestUploadDialog({
  examId,
  opened,
  onClose,
}: {
  examId: string;
  opened: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const form = useForm({
    initialValues: {
      kind: "" as FunctionalTestKind | "",
      summary_text: "",
      files: [] as File[],
    },
    validate: {
      kind: (v) => (v ? null : t("validation.required")),
      summary_text: (v) => (v.trim().length > 0 ? null : t("validation.required")),
    },
  });

  const mutation = useMutation({
    mutationFn: (vals: { kind: FunctionalTestKind; summary_text: string; files: File[] }) =>
      uploadFunctionalTest(examId, vals),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["functional-tests", examId] });
      notifications.show({ color: "green", message: t("functional_tests.uploaded") });
      form.reset();
      onClose();
    },
  });

  const errorMessage = (() => {
    if (!mutation.error) return null;
    if (mutation.error instanceof ApiError) return mutation.error.message;
    return t("auth.network_error");
  })();

  return (
    <Modal
      opened={opened}
      onClose={() => {
        form.reset();
        onClose();
      }}
      title={t("functional_tests.upload_title")}
      size="md"
    >
      {errorMessage && (
        <Alert color="red" icon={<IconAlertCircle size={16} />} mb="md">
          {errorMessage}
        </Alert>
      )}
      <form
        onSubmit={form.onSubmit((vals) => {
          if (!vals.kind) return;
          mutation.mutate({
            kind: vals.kind,
            summary_text: vals.summary_text,
            files: vals.files,
          });
        })}
      >
        <Stack>
          <Select
            label={t("functional_tests.fields.kind")}
            placeholder={t("placeholder.select")}
            required
            data={KINDS.map((k) => ({ value: k, label: t(`functional_tests.kinds.${k}`) }))}
            {...form.getInputProps("kind")}
          />
          <Textarea
            label={t("functional_tests.fields.summary")}
            placeholder={t("placeholder.summary")}
            required
            minRows={3}
            autosize
            {...form.getInputProps("summary_text")}
          />
          <FileInput
            label={t("functional_tests.fields.files")}
            description={t("functional_tests.files_hint")}
            placeholder={t("placeholder.select")}
            multiple
            accept="image/jpeg,image/png,image/webp,application/pdf"
            leftSection={<IconUpload size={16} />}
            {...form.getInputProps("files")}
          />
          <Group justify="flex-end" mt="sm">
            <Button variant="subtle" color="gray" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" loading={mutation.isPending} radius="xl">
              {t("functional_tests.submit")}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
